// root '../../../';

// dependencies
import { Child } from 'pym.js';
import Zion from './utils/Zion';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic

// styles
import './styles/graphic.scss';

/* develblock:start */

import * as d3 from 'd3';
import { format } from 'd3-format';
import { mean, median } from 'd3-array';
import { select } from 'd3-selection';
import meta from './data/meta.json';
import { stylePremium } from './utils/helpers';
import { bandAxis, timeAxis, linearAxis } from '../../../factory/axes';

import './styles/graphic.scss';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  monthsShort: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
  meridiem: (hour, minute, isLowercase) =>
    // OPTIONAL, AM/PM
    (hour >= 12 ? 'p.m.' : 'a.m.'),

});


// formatD3Time();
d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

const fmtComma = format(',');
const fmtSI = format('.1s');
const fmtThou = format('~s');
const fmt1f = format('.1f');

function roundToNearestHundred(number) {
  return Math.round(number / 100) * 100;
}

const fmtThouRounded = n => ((n < 1000) ? n : `${fmt1f(roundToNearestHundred(n) / 1000)}K`);


const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}


let minValue;
let maxValue;
let firstItem;
let lastItem;
let maxItem;
let maxIndex;
let shortTimestamp;
let lastYearMean;
let lastYear;


// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};


const CNNPolitics = false;
const CNNBusiness = false;

stylePremium(CNNPolitics, CNNBusiness);


const params = window.location.search.substring(1);
const searchParams = new URLSearchParams(params);

const defaultDays = 30;
const days = searchParams.get('days');
const isValidDays = +days > 6 && +days < 91;
const daysToUse = (isValidDays) ? +days : defaultDays;
const useSmallTime = !!searchParams.get('sm');


let data;
d3.json('https://ix.cnn.io/data/flight-cancellations/us_cancellations_history.json').then((jsondata) => {
  let curYear = '';

  lastYear = dayjs().subtract(1, 'year').format('YYYY');

  const historicalData = jsondata.data.filter(
    d => d.date.split('-')[0] === lastYear,
  );

  lastYearMean = Math.round(mean(historicalData, d => d.us_delays));

  console.log(`${lastYear} daily average:`, lastYearMean);

  data = jsondata.data.map(
    (d) => {
      const dayjs_date = dayjs.utc(d.date);
      const yr = d.date.split('-')[0];
      const hasYear = yr !== curYear;
      curYear = yr;
      return {
        ...d,
        date: d3.timeParse('%Y-%m-%d')(d.date),
        year: yr,
        hasYear,
        amt: d.us_delays,
        label: dayjs_date.utc().format('MMM D, YYYY'),
        month: dayjs_date.utc().format('M'),
        month_year: dayjs_date.utc().format('YYYY-MM'),
        full_date: dayjs_date.utc().format('YYYY-MM-DD'),
        date_str: dayjs_date.utc().format('YYYYMMDD'),
      };
    },
  )
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .slice(-daysToUse);

  firstItem = data[0];
  lastItem = data[data.length - 1];
  minValue = Math.min(0, d3.min(data, d => d.amt));
  const maxActualValue = d3.max(data, d => +d.amt);
  // giving more vertical space to the chart than the actual max
  maxValue = maxActualValue * 1.2;
  // note the max item to know where to annotate
  maxItem = data.find(d => +d.amt === maxActualValue);
  maxIndex = data.findIndex(d => +d.amt === maxActualValue);

  const tz = 'America/New_York';

  shortTimestamp = jsondata.lastUpdatedStr.split('at ')[1];

  const timeStampFootnoote = d3.select('p#timestamp')
    .html(`Data last updated ${jsondata.lastUpdatedStr}`);

  draw();

  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');


  const initialWidth = container.node().clientWidth;
  const initialHeight = 400;
  const isMobile = initialWidth <= 500;

  const margins = {
    top: 30,
    right: (isMobile) ? 58 - 15 : 55 - 15,
    bottom: 25,
    left: (isMobile) ? 30 : 50,
  };

  const width = initialWidth - margins.right - margins.left;


  const height = 400;
  const padding = 0.15;


  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xAxisConfig = {
    orientation: 'bottom',
    range: [0, chartWidth],
    domain: data.map(d => d.date_str),
    padding,
  };

  const yAxisConfig = {
    orientation: 'left',
    range: [chartHeight, 0],
    domain: [Math.min(0, minValue), maxValue],
    nice: false,
  };

  const { axis: xAxis, scale: x } = bandAxis(xAxisConfig);
  const { axis: yAxis, scale: y } = linearAxis(yAxisConfig);

  const yTicks = isMobile ? 5 : 5;

  const leading = 1.2;

  const formatNum = amt => (isMobile
    ? (Math.abs(amt) > 999
      ? fmtThouRounded(amt).toUpperCase()
      : fmtThouRounded(amt)
    )
    : (Math.abs(amt) > 9999
      ? fmtThouRounded(amt).toUpperCase()
      : fmtThouRounded(amt)
    ));

  const drawAnnotation = (item, halfWidth, classList, align, toplines) => {
    const xOffset = align === 'start' ? -3.6 : (align === 'middle' ? 0 : 2.4);
    const textAnchor = align === 'start' ? 'start' : (align === 'middle' ? 'middle' : 'end');
    const annotation = g.append('g')
      .attr('class', `annotation halo ${classList}`)
      .attr('transform', d => `translate(${[x(item.date_str) + halfWidth, y(item.amt < 0 ? 0 : item.amt)]})`);

    annotation.append('line')
      .attr('x1', 0)
      .attr('y1', -25)
      .attr('x2', 0)
      .attr('y2', -1)
      .style('stroke-width', 1)
      .style('stroke', '#999')
      .style('fill', 'none');


    if (toplines) {
      toplines.forEach(
        (d, i) => {
          const cls = `annotation-date ${(d[1]) ? d[1] : ''}`;
          const txt = d[0];
          const yOffset = d[2] || `${-2.5 - ((i + 1) * leading)}em`;
          annotation.append('text')
            .attr('dx', '10px')
            .attr('class', cls)
            .text(txt)
            .attr('dy', yOffset)
            .attr('dx', xOffset)
            .attr('text-anchor', textAnchor);
        },
      );
    }


    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-value')
      .text(fmtComma(item.amt))
      .attr('dy', '-2.5em')
      .attr('dx', xOffset)
      .attr('text-anchor', textAnchor);
  };


  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  const formatMonth = d3.timeFormat('%b');

  const xGrid = g.append('g')
    .attr('class', 'x grid')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(
      xAxis(x)
        .tickFormat(d => `${dayjs.utc(d).format('MMM D')}`),
    );


  xGrid.selectAll('text')
    .attr('y', 10);


  const yGrid = g.append('g')
    .attr('class', 'y grid')
    .call(yAxis(y)
      .ticks(yTicks)
      .tickSize(-width)
      .tickFormat(d => (isMobile
        ? (Math.abs(d) > 999
          ? fmtThou(d).toUpperCase()
          : fmtComma(d)
        )
        : (Math.abs(d) > 9999 * 100000
          ? fmtThou(d).toUpperCase()
          : fmtComma(d)
        ))));

  yGrid.selectAll('text')
    .attr('x', -5)
    .filter(d => d === 0 && minValue >= 0)
    .remove(); // hide zero

  function raise() {
    d3.select(this).raise();
  }

  let tooltipEl;
  const tooltip = (d) => {
    if (!tooltipEl) return;
    if (d) {
      tooltipEl.text(formatNum(d.amt));
      tooltipEl.attr('transform', `translate(${x(d.date_str) + (x.bandwidth() / 2)} ${y(+d.amt)})`);
      // tooltipEl.attr('transform', 'translate(0 0)');
      tooltipEl.style.visibility = 'visible';
    } else {
      tooltipEl.text('');
      tooltipEl.style.visibility = 'hidden';
    }
  };

  const group = g.append('g')
    .attr('class', 'data');

  const bars = group.append('g')
    .attr('class', 'bars');

  const bar = bars.selectAll('.bar')
    .data(data)
    .enter().append('g')
    .attr('class', (d, i) => (i === maxIndex || i === data.length - 1 ? 'bar highlight' : 'bar'))
    .attr('role', 'list')
    .attr('transform', d => `translate(${[x(d.date_str), y(d.amt < 0 ? 0 : d.amt)]})`)
    .on('mouseover', (d, i) => {
      if (i !== maxIndex && i < data.length - 1) {
        tooltip(d);
      } else {
        tooltip();
      }
    })
    .on('mouseout', () => tooltip());

  bar.append('title')
    .text(d => `${d.label} (${fmtComma(d.amt)})`);

  bar.append('rect')
    .attr('width', x.bandwidth())
    .attr('height', d => Math.abs(y(d.amt) - y(0)));


  if (y.domain()[0] < 0) {
    // zero line
    g.append('line')
      .attr('role', 'presentation')
      .attr('class', 'zero-bar')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', y(0))
      .attr('y2', y(0));
  }

  // historical average
  g.append('line')
    .attr('role', 'presentation')
    .attr('class', 'zero-bar historical-mean')
    .style('stroke-dasharray', ('3, 4'))
    .attr('x1', 0)
    .attr('x2', width)
    .attr('y1', y(lastYearMean))
    .attr('y2', y(lastYearMean));

  g.append('text')
    .attr('x', 20)
    .attr('y', y(lastYearMean))
    .attr('dy', '-.5em')
    .attr('class', 'historical-label halo-sm')
    .text(`${lastYear} daily average`);

  if (maxIndex < data.length - 1) {
    const annotationHigh = [
      [maxItem.label.split(',')[0]],
      [`${daysToUse}-day high`],
    ];

    drawAnnotation(maxItem, x.bandwidth() / 2, 'max', 'middle', annotationHigh);
  }

  // Use small timestamp size
  if (useSmallTime) {
    drawAnnotation(data[data.length - 1], x.bandwidth() / 2, 'current', 'start', [
      [shortTimestamp, 'timestamp', -45],
      [lastItem.label.split(',')[0], '', -57],
    ]);
  } else {
    // regular timestamp size
    drawAnnotation(data[data.length - 1], x.bandwidth() / 2, 'current', 'start', [
      // [shortTimestamp],
      [lastItem.label.split(',')[0]],
    ]);
  }

  tooltipEl = svg
    .append('g')
    .attr('transform', `translate(${margins.left} ${margins.top} )`)
    .append('text')
    .attr('dy', '-.5em')
    .attr('class', 'value in halo tooltipTxt')
    .attr('text-anchor', 'middle')
    .text('');


  window.pymChild.sendHeight();

  d3.select('#root')
    .attr('class', 'ready');
}
